import { push } from 'connected-react-router';
import memoizeOne from 'memoize-one';
import React, { Fragment, ReactNode } from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { checkedFetchAccessTokenIfNeeded, fetchAccessToken } from '~/AccessToken';
import { GROUP_TYPE, ModuleType, UserProductGroup, UserProductGroupErrorResponse } from '~/Api/User/ProductGroup';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage, flashSuccessMessage, flashWarningMessage } from '~/FlashMessage';
import flashMessageStyles from '~/FlashMessage/styles.less';
import { logGoogleTagManagerCreateProductGroup } from '~/GoogleTagManager';
import logGoogleTagManagerCreateVirtualProductGroup from '~/GoogleTagManager/logGoogleTagManagerCreateVirtualProductGroup';
import {
	buildGroupInitialUrl,
	createGroup,
	createGroupFromVirtualPack,
	Filter,
	redirectFromDeletedGroup,
	selectGroups,
} from '~/ProductGroup';
import { ApplicationState, State, ThunkAction, ThunkDispatch } from '~/Store';
import { Link } from '~/UI/Button';

import CreateModal, { EnabledModule } from './CreateModal';

const memoizedSelectGroups = memoizeOne(selectGroups);

interface Props {
	groupType: string;
	templateGroup?: UserProductGroup | null;
	parentId?: string | null;
	initialInputValue?: string;
	initialModule?: ModuleType | null;
	isFetching?: boolean;
	onSave(group: UserProductGroup, options?: { preventGroupAdditions?: boolean }): void;
}

const mapEnabledModules = memoizeOne((systemModules: Set<string>): EnabledModule[] =>
	[
		{ name: 'CZ Max Price', type: ModuleType.czMaxPrice },
		{ name: 'CZ Reimbursement', type: ModuleType.czReimbursement },
		{ name: 'SK Max Price', type: ModuleType.skMaxPrice },
		{ name: 'SI Max Price', type: ModuleType.siMaxPrice },
		{ name: 'HR Max Price', type: ModuleType.hrMaxPrice },
		{ name: 'BG Max Price', type: ModuleType.bgMaxPrice },
		{ name: 'RO Max Price', type: ModuleType.roMaxPrice },
		{ name: 'LT Max Price', type: ModuleType.ltMaxPrice },
	].filter((enabledModule) => systemModules.has(enabledModule.type))
);

const mapStateToProps = (
	state: State,
	props: Props
): {
	title: React.ReactNode;
	namePlaceholder: string;
	initialInputValue: string;
	initialModule: ModuleType | null;
	isFetching: boolean;
	enabledModules: EnabledModule[] | null;
	templateGroup?: UserProductGroup | null;
	isCzReimbursmentPrefillAllowed: boolean;
} => {
	const { groupType, parentId = null, templateGroup } = props;
	const { accessToken } = state.accessToken;
	const systemModules = accessToken?.subscription?.systemModules ?? null;
	const groupsState = memoizedSelectGroups(state.productGroup, new Filter(parentId, null));

	return {
		title: (templateGroup ? 'Copy ' : 'Create new ') + (groupType === GROUP_TYPE ? 'folder' : 'list'),
		namePlaceholder: groupType === GROUP_TYPE ? 'Folder name' : 'List name',
		initialInputValue: props.initialInputValue || (templateGroup ? `${templateGroup.name} - copy` : ''),
		initialModule: props.initialModule ?? (templateGroup && templateGroup.isGroup() ? templateGroup.moduleType : null),
		isFetching: props.isFetching || state.productGroup.createRequest !== null || groupsState.status === 'fetching',
		enabledModules: systemModules !== null ? mapEnabledModules(systemModules) : null,
		isCzReimbursmentPrefillAllowed: systemModules?.has('cz-reimbursement-prefill-assistant') ?? false,
	};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<AnyAction>, props: Props) => {
	return {
		onFetch: async () => {
			if (props.groupType === GROUP_TYPE) {
				const response = await dispatch(checkedFetchAccessTokenIfNeeded());
				if (response && !response.accessToken?.subscription) {
					dispatch(
						flashWarningMessage(
							"Application functionality may be limited, because you don't have any subscription",
							false
						)
					);
				}
			}
		},
		onSave: (
			name: string,
			module: ModuleType | null,
			parentGroup: UserProductGroup | null,
			czReimbursementLists: Array<{ codes: string[] }> | null = null
		) => {
			const { templateGroup, parentId, onSave } = props;
			const templateId = templateGroup ? templateGroup.id : null;
			const result = { moduleError: null };

			return (
				dispatch(
					createGroup(
						name,
						props.groupType,
						module,
						parentGroup ? parentGroup.id : parentId,
						templateId,
						czReimbursementLists
					)
				)
					.then((response) => {
						logGoogleTagManagerCreateProductGroup(response.productGroup.id, name);
						onSave(response.productGroup);
						if (templateGroup) {
							dispatch(flashGroupCreatedMessage(response.productGroup, parentGroup, templateGroup));
						}
						return result;
					})
					// @ts-expect-error strange error
					.catch((response) => {
						if (!(response instanceof UserProductGroupErrorResponse)) {
							dispatch(handleError(response));
							return result;
						}

						if (response.handleProductGroupCustomerDoesNotHaveSubscriptionForSystemModule()) {
							return dispatch(fetchAccessToken()).then(() => ({
								...result,
								moduleError: 'Please make sure that selected module is allowed for your user account',
							}));
						}

						if (response.handleProductGroupParentNotFound()) {
							dispatch(push('/product-groups/'));
							dispatch(flashErrorMessage('Could not create a list, because parent folder was removed'));
						}

						if (response.handleProductGroupTemplateNotFound()) {
							dispatch(templateGroup ? redirectFromDeletedGroup(templateGroup) : push('/product-groups/'));
							dispatch(flashErrorMessage('Could not copy a group, because original group was removed'));
						}

						dispatch(handleError(response));
						return result;
					})
					.catch((response) => {
						dispatch(handleError(response));
						return result;
					})
			);
		},
		onCreateFromVirtualPack: (
			name: string,
			parentGroup: UserProductGroup | null,
			virtualPackId: string,
			czReimbursementLists: Array<{ codes: string[] }>
		) => {
			const { templateGroup, parentId, onSave } = props;
			const result = { moduleError: null };

			return (
				dispatch(
					createGroupFromVirtualPack(name, parentGroup ? parentGroup.id : parentId, virtualPackId, czReimbursementLists)
				)
					.then((response) => {
						logGoogleTagManagerCreateVirtualProductGroup(response.productGroup.id, name, virtualPackId);
						onSave(response.productGroup, { preventGroupAdditions: true });
						if (templateGroup) {
							dispatch(flashGroupCreatedMessage(response.productGroup, parentGroup, templateGroup));
						}
						return result;
					})
					// @ts-expect-error strange error
					.catch((response) => {
						if (!(response instanceof UserProductGroupErrorResponse)) {
							dispatch(handleError(response));
							return result;
						}

						if (response.handleProductGroupCustomerDoesNotHaveSubscriptionForSystemModule()) {
							return dispatch(fetchAccessToken()).then(() => ({
								...result,
								moduleError: 'Please make sure that selected module is allowed for your user account',
							}));
						}

						if (response.handleProductGroupParentNotFound()) {
							dispatch(push('/product-groups/'));
							dispatch(flashErrorMessage('Could not create a list, because parent folder was removed'));
						}

						if (response.handleProductGroupTemplateNotFound()) {
							dispatch(templateGroup ? redirectFromDeletedGroup(templateGroup) : push('/product-groups/'));
							dispatch(flashErrorMessage('Could not create a group, because original group was removed'));
						}

						dispatch(handleError(response));
						return result;
					})
					.catch((response) => {
						dispatch(handleError(response));
						return result;
					})
			);
		},
	};
};

function flashGroupCreatedMessage(
	group: UserProductGroup,
	parentGroup: UserProductGroup | null,
	templateGroup?: UserProductGroup | null
): ThunkAction<void, any> {
	return (dispatch, getState) => {
		const state = getState();

		dispatch(
			flashSuccessMessage(
				<span>
					Product {templateGroup?.isProductList() ? 'list ' : 'group '}
					<strong>{templateGroup?.name}</strong> was copied{' '}
					{parentGroup ? <Fragment>to {renderGroupLink(parentGroup, state)}</Fragment> : null}
					as {renderGroupLink(group, state)}
				</span>
			)
		);
	};
}

function renderGroupLink(group: UserProductGroup, state: ApplicationState): ReactNode {
	return (
		<Link styles={flashMessageStyles} title={group.name} to={buildGroupInitialUrl(group, state)}>
			{group.name}
		</Link>
	);
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateModal);
